import { useEffect, useRef } from 'react';
import { Pagination } from 'src/components/pagination/Pagination';
import { DataTestID } from 'src/utils';

import { CustomTable } from '../CustomTable';
import { FlexTableWrapper } from '../CustomTable.styled';
import { CustomTableWithPaginationProps } from './CustomTableWithPagination.interface';
import { TableControlsHeader } from './TableControlsHeader/TableControlsHeader';

export const PAGINATION_HEIGHT = 64;

export const CustomTableWithPagination = <
  T extends Record<string, any> = Record<string, any>,
>({
  columns,
  rows,
  loading,
  stickyHeader = true,
  emptyMessage,
  renderActions,
  renderTags,
  page,
  totalPages,
  totalCount,
  rowsPerPage,
  onPageChange,
  showTableControls,
  onSortChange,
  selectedSortOption,
  sortItems,
  overrideHeight,
  onRowsPerPageChange,
  renderHeadActions,
  renderCollapseContent,
  visibleControls,
  selectedCount,
  onClearSelection,
  expandedRowIndex,
  onRowExpand,
  tableHeadRenderer,
  customCellRenderer,
  renderAccordionRow,
  tableCellHeadProps,
  tableBodyRowProps,
  tableContainerHeight,
  onRowClick,
  bodyBackgroundColor,
}: CustomTableWithPaginationProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [page, loading]);

  return (
    <FlexTableWrapper
      data-testid={DataTestID.CustomTableWithPagination}
      {...(tableContainerHeight && { $height: tableContainerHeight })}>
      {showTableControls && !loading && (
        <TableControlsHeader
          page={page}
          totalCount={totalCount}
          onSortChange={onSortChange}
          selectedSortOption={selectedSortOption}
          sortItems={sortItems}
          rowsPerPage={rowsPerPage ? rowsPerPage : 20}
          onRowsPerPageChange={onRowsPerPageChange}
          visibleControls={visibleControls}
          selectedCount={selectedCount}
          onClearSelection={onClearSelection}
        />
      )}
      <CustomTable<T>
        columns={columns}
        rows={rows}
        loading={loading}
        stickyHeader={stickyHeader}
        emptyMessage={emptyMessage}
        renderActions={renderActions}
        renderTags={renderTags}
        overrideHeight={loading ? '0px' : overrideHeight}
        renderHeadActions={renderHeadActions}
        renderCollapseContent={renderCollapseContent}
        expandedRowIndex={expandedRowIndex}
        onRowExpand={onRowExpand}
        tableHeadRenderer={tableHeadRenderer}
        customCellRenderer={customCellRenderer}
        renderAccordionRow={renderAccordionRow}
        tableCellHeadProps={tableCellHeadProps}
        tableBodyRowProps={tableBodyRowProps}
        scrollContainerRef={scrollContainerRef}
        onRowClick={onRowClick}
        bodyBackgroundColor={bodyBackgroundColor}
      />
      <Pagination
        page={page}
        totalPages={totalPages}
        onChange={onPageChange}
        loading={loading}
      />
    </FlexTableWrapper>
  );
};
